import './tab-navigation.scss';

class TabNavigation {
    constructor ($element, options) {
        const defaults = {
            initAttr: 'data-tab-navigation',
            tabsAttr: 'tabs',
            navAttr: 'nav',
            scrollerAttr: 'scroller',
            listAttr: 'list',
            navItemAttr: 'nav-item',
            toggleAttr: 'toggle',
            controlsAttr: 'controls',
            prevAttr: 'prev',
            nextAttr: 'next',
            offset: 16
        };

        this.settings = Object.assign({}, defaults, options);

        this.$tabNavigation = $element;
        this.$tabNavigationToggles = this.$tabNavigation.querySelectorAll(`[${this.settings.initAttr}="${this.settings.toggleAttr}"]`);
        this.$tabNavigationNav = this.$tabNavigation.querySelector(`[${this.settings.initAttr}="${this.settings.navAttr}"]`);
        this.$tabNavigationList = this.$tabNavigation.querySelector(`[${this.settings.initAttr}="${this.settings.listAttr}"]`);
        this.$tabNavigationScroller = this.$tabNavigationNav.querySelector(`[${this.settings.initAttr}="${this.settings.scrollerAttr}"]`);
        this.$tabNavigationItems = this.$tabNavigationNav.querySelectorAll(`[${this.settings.initAttr}="${this.settings.navItemAttr}"]`);
        this.$tabNavigationTabs = this.$tabNavigation.querySelector(`[${this.settings.initAttr}="${this.settings.tabsAttr}"]`);
        this.$tabNavigationControls = this.$tabNavigationNav.querySelector(`[${this.settings.initAttr}="${this.settings.controlsAttr}"]`);
        this.$tabNavigationPrevBtn = this.$tabNavigationControls.querySelector(`[${this.settings.initAttr}="${this.settings.prevAttr}"]`);
        this.$tabNavigationNextBtn = this.$tabNavigationControls.querySelector(`[${this.settings.initAttr}="${this.settings.nextAttr}"]`);
        this.activeIndex = 0;
        this.resizeTimer = null;

        this.changeEvent = new CustomEvent('change', {
            view: window,
            bubbles: true,
            cancelable: false
        });
    }

    initialize () {
        if (this.$tabNavigation) {
            this.checkControls();
            this.setEvents();

            if (window.location.hash) {
                const tabHash = window.location.hash.split('#')[1];
                const $tabToggle = document.querySelector('[rel="' + tabHash + '"]');

                if ($tabToggle) {
                    $tabToggle.checked = true;
                    $tabToggle.dispatchEvent(this.changeEvent);
                }
            }
        }
    }

    setEvents () {
        this.$tabNavigationItems.forEach(($navItem) => {
            const $label = $navItem.firstElementChild;

            $label.addEventListener('keydown', (e) => {
                if (e.keyCode === 13 || e.keyCode === 32) {
                    e.preventDefault();
                    const id = $label.getAttribute('for');
                    const $toggle = this.$tabNavigationTabs.querySelector('input[id="' + id + '"]');

                    if ($toggle) {
                        $toggle.checked = true;
                        $toggle.dispatchEvent(this.changeEvent);
                    }
                }
            });
        });

        this.$tabNavigationTabs.addEventListener('change', (e) => {
            const $target = e.target;

            if ($target.getAttribute(this.settings.initAttr) === this.settings.toggleAttr) {
                this.setActiveTab($target.getAttribute('id'));
                this.setUrlHash($target.getAttribute('rel'));
            }
        });

        this.$tabNavigationPrevBtn.addEventListener('click', (e) => {
            if (this.activeIndex !== 0) {
                this.setActiveToggle(this.activeIndex - 1);
            }

            e.preventDefault();
        });

        this.$tabNavigationNextBtn.addEventListener('click', (e) => {
            if (this.activeIndex < this.$tabNavigationToggles.length - 1) {
                this.setActiveToggle(this.activeIndex + 1);
            }

            e.preventDefault();
        });

        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer);

            this.resizeTimer = window.setTimeout(() => {
                this.checkControls();
            }, 150);
        });
    }

    setActiveToggle (index) {
        const $toggle = this.$tabNavigationToggles[index];
        $toggle.checked = true;

        $toggle.dispatchEvent(this.changeEvent);
    }

    toggleFocusElements ($element, status) {
        const id = $element.firstElementChild.getAttribute('for');
        const $input = this.$tabNavigationTabs.querySelector('input[id="' + id + '"]');
        const $panel = $input?.nextElementSibling;

        if ($input && $panel) {
            if (status === 'enable' || status === true || status === 1) {
                $input.setAttribute('aria-checked', 'true');
                $panel.setAttribute('aria-expanded', 'true');
                $panel.setAttribute('aria-hidden', 'false');
            } else {
                $input.setAttribute('aria-checked', 'false');
                $panel.setAttribute('aria-expanded', 'false');
                $panel.setAttribute('aria-hidden', 'true');
            }

            const $focusableElements = $panel.querySelectorAll(
                'a[href], button, textarea, input, select'
            );

            if ($focusableElements.length > 0) {
                $focusableElements.forEach((element) => {
                    if (status === 'enable' || status === true || status === 1) {
                        element.setAttribute('tabindex', '0');
                    } else {
                        element.setAttribute('tabindex', '-1');
                    }
                });
            }
        }
    }

    setActiveTab (activeId) {
        const $activeItem = this.$tabNavigationNav.querySelector('label[for="' + activeId + '"]').parentNode;

        for (let a = 0; a < this.$tabNavigationItems.length; a++) {
            const $currentItem = this.$tabNavigationItems[a];

            if ($currentItem === $activeItem) {
                this.toggleFocusElements($currentItem, 'enable');

                this.activeIndex = a;
                const currentItemOffsetLeft = $currentItem.offsetLeft;
                const firstItemOffsetLeft = this.$tabNavigationItems[0].offsetLeft;
                let scrollLeft = currentItemOffsetLeft - firstItemOffsetLeft + this.settings.offset;

                if (a === 0) {
                    scrollLeft = 0;
                    this.$tabNavigationPrevBtn.classList.add('tab-navigation__control--disabled');
                    this.$tabNavigationPrevBtn.setAttribute('aria-disabled', 'true');
                    this.$tabNavigationPrevBtn.setAttribute('tabindex', '-1');
                } else if (a === this.$tabNavigationItems.length - 1) {
                    this.$tabNavigationNextBtn.classList.add('tab-navigation__control--disabled');
                    this.$tabNavigationNextBtn.setAttribute('aria-disabled', 'true');
                    this.$tabNavigationNextBtn.setAttribute('tabindex', '-1');
                } else {
                    this.$tabNavigationPrevBtn.classList.remove('tab-navigation__control--disabled');
                    this.$tabNavigationPrevBtn.setAttribute('aria-disabled', 'false');
                    this.$tabNavigationPrevBtn.setAttribute('tabindex', '0');
                    this.$tabNavigationNextBtn.classList.remove('tab-navigation__control--disabled');
                    this.$tabNavigationNextBtn.setAttribute('aria-disabled', 'false');
                    this.$tabNavigationNextBtn.setAttribute('tabindex', '0');
                }

                $currentItem.classList.add('tab-navigation__list-item--active');
                this.$tabNavigationScroller.scrollTo({
                    top: 0,
                    left: scrollLeft,
                    behavior: 'smooth'
                });
            } else {
                this.toggleFocusElements($currentItem, 'disable');
                $currentItem.classList.remove('tab-navigation__list-item--active');
            }
        }
    }

    setUrlHash (slug) {
        if (slug) {
            window.location.hash = slug;
        }
    }

    checkControls () {
        const navWidth = this.$tabNavigationNav.offsetWidth;
        const scrollerWidth = this.$tabNavigationScroller.scrollWidth;

        if (navWidth < scrollerWidth) {
            this.$tabNavigationControls.classList.remove('tab-navigation__controls--hidden');
            this.$tabNavigationControls.setAttribute('aria-hidden', 'false');
        } else {
            this.$tabNavigationControls.classList.add('tab-navigation__controls--hidden');
            this.$tabNavigationControls.setAttribute('aria-hidden', 'true');
        }
    }
}

export { TabNavigation };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$tabNavigation = $context.querySelectorAll('[data-tab-navigation="root"]');

        for (let i = 0; i < $$tabNavigation.length; i++) {
            const tabNavigation = new TabNavigation($$tabNavigation[i]);
            tabNavigation.initialize();
        }
    }
});
